import { CheckCircleFilled } from "@ant-design/icons";
import { Alert, Button, Checkbox, Form, Input, Radio, Space, Typography, message } from "antd";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type SetupWorkflow,
  type WorkflowOverride,
  type WorkflowQuestion,
  useSaveWorkflowMutation,
} from "../../service/onboarding.service";
import styles from "./setup.module.scss";
import { apiErrorMessage } from "./setup.shared";

const { Text } = Typography;

const OTHER = "other";
const AUTOSAVE_MS = 800;
const REQUIRED = [{ required: true, message: "Please answer this one." }];

type Flow = Omit<WorkflowOverride, "visit_type"> & { different?: boolean };
type Values = Record<string, unknown> & { overrides?: Record<string, Flow | undefined> };

type Props = {
  token: string;
  workflow: SetupWorkflow;
};

const toValues = ({ answers, visit_type_overrides }: SetupWorkflow): Values => ({
  ...answers,
  overrides: Object.fromEntries(
    visit_type_overrides.map(({ visit_type, ...flow }) => [visit_type, { ...flow, different: true }]),
  ),
});

const toPayload = ({ overrides = {}, ...answers }: Values) => ({
  answers,
  visit_type_overrides: Object.entries(overrides).flatMap(([visit_type, flow]) => {
    if (!flow?.different) return [];
    const { different, ...rest } = flow;
    return [{ visit_type, ...rest }];
  }),
});

const isVisible = ({ show_if: rule }: WorkflowQuestion, values: Values) => {
  if (!rule) return true;
  const selected = (values[rule.question] as string[] | undefined) ?? [];
  return rule.includes ? selected.includes(rule.includes) : selected.length >= (rule.min_selected ?? 0);
};

/** An exclusive option ("None") replaces the rest; picking anything else drops it. */
const keepExclusive =
  (question: WorkflowQuestion) =>
  (next: string[] = [], previous: string[] = []) => {
    const exclusive = question.options.filter((option) => option.exclusive).map((option) => option.value);
    const added = next.find((value) => !previous.includes(value));
    return added && exclusive.includes(added) ? [added] : next.filter((value) => !exclusive.includes(value));
  };

/** One multiple-choice question, plus the text field its "Other" option opens. */
function ChoiceField({
  question,
  path,
  label,
  required,
  picked,
}: {
  question: WorkflowQuestion;
  path: string[];
  label: string;
  required: boolean;
  picked: unknown;
}) {
  const options = question.options.map(({ value, label: text }) => ({ value, label: text }));
  const isMulti = question.type === "multi";
  return (
    <>
      <Form.Item
        label={label}
        name={path}
        rules={required ? REQUIRED : undefined}
        normalize={isMulti ? keepExclusive(question) : undefined}
      >
        {isMulti ? (
          <Checkbox.Group className={styles.choices} options={options} />
        ) : (
          <Radio.Group className={styles.choices} options={options} />
        )}
      </Form.Item>
      {Array.isArray(picked) && picked.includes(OTHER) ? (
        <Form.Item
          name={[...path.slice(0, -1), `${path[path.length - 1]}_other`]}
          rules={[{ required: true, whitespace: true, message: "Please tell us what it is." }]}
        >
          <Input placeholder="Tell us what it is" maxLength={200} />
        </Form.Item>
      ) : null}
    </>
  );
}

/** Step 4 — how the practice runs a visit. */
export default function WorkflowForm({ token, workflow }: Props) {
  const [save] = useSaveWorkflowMutation();
  const [form] = Form.useForm<Values>();
  const initialValues = useMemo(() => toValues(workflow), [workflow]);
  const values: Values = Form.useWatch([], form) ?? initialValues;
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSave = useRef<Promise<unknown>>(Promise.resolve());
  const submitted = !!workflow.submitted_at;
  const byId = useMemo(() => Object.fromEntries(workflow.questions.map((q) => [q.id, q])), [workflow.questions]);

  // Every answer saves as a draft, so the customer can leave and come back.
  const autosave = useMemo(
    () =>
      debounce(() => {
        lastSave.current = save({ token, ...toPayload(form.getFieldsValue(true)), submit: false })
          .unwrap()
          .catch(() => undefined);
      }, AUTOSAVE_MS),
    [save, token, form],
  );
  // Collapsing the step unmounts this form; don't drop the last keystrokes with it.
  useEffect(() => () => autosave.flush(), [autosave]);

  const onFinish = async () => {
    autosave.cancel();
    setIsSubmitting(true);
    try {
      // A draft still in flight must land first, or its reply would overwrite this one.
      await lastSave.current;
      await save({ token, ...toPayload(form.getFieldsValue(true)), submit: true }).unwrap();
      message.success(submitted ? "Saved — we've let our team know." : "Thanks — that's everything we need here.");
      setIsEditing(false);
    } catch (err) {
      message.error(apiErrorMessage(err, "We couldn't save that. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted && !isEditing) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleFilled />}
        message={workflow.headline || "Thanks — we know how your visits run"}
        description={
          <>
            <dl className={styles.answerList}>
              {workflow.summary.map(({ question, answer }) => (
                <div key={question}>
                  <dt>{question}</dt>
                  <dd>{answer}</dd>
                </div>
              ))}
            </dl>
            {workflow.editable ? null : (
              <Text type="secondary">These are locked now that you're live. Get in touch and we'll update them.</Text>
            )}
          </>
        }
        action={
          workflow.editable ? (
            <Button size="small" type="link" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          ) : null
        }
      />
    );
  }

  const visitTypes = ((values.E1 as string[] | undefined) ?? []).map((value) => ({
    value,
    label:
      value === OTHER
        ? (values.E1_other as string) || "Other"
        : byId.E1.options.find((option) => option.value === value)?.label ?? value,
  }));

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={initialValues}
      scrollToFirstError
      onValuesChange={() => {
        if (!submitted) autosave();
      }}
      onFinish={onFinish}
    >
      {workflow.questions.map((question, index) => {
        if (!isVisible(question, values)) return null;
        const label =
          question.required || question.type === "overrides" ? question.prompt : `${question.prompt} (optional)`;
        return (
          <div key={question.id}>
            {question.group !== workflow.questions[index - 1]?.group ? (
              <p className={`${styles.sectionLabel} ${styles.groupLabel}`}>{question.group}</p>
            ) : null}
            {question.type === "text" ? (
              <Form.Item label={label} name={question.id}>
                <Input.TextArea rows={3} maxLength={2000} />
              </Form.Item>
            ) : question.type === "overrides" ? (
              <Form.Item label={label}>
                {visitTypes.map((visitType) => {
                  const flow = values.overrides?.[visitType.value];
                  return (
                    <div key={visitType.value}>
                      <Form.Item
                        label={visitType.label}
                        name={["overrides", visitType.value, "different"]}
                        // Untouched means "same as above".
                        getValueProps={(different) => ({ value: !!different })}
                        className={styles.overrideChoice}
                      >
                        <Radio.Group
                          options={[
                            { value: false, label: "Same as above" },
                            { value: true, label: "Different" },
                          ]}
                        />
                      </Form.Item>
                      {flow?.different ? (
                        <div className={styles.overrideBody}>
                          {[byId.B1, byId.B2].map((base) => (
                            <ChoiceField
                              key={base.id}
                              question={base}
                              path={["overrides", visitType.value, base.id]}
                              label={base.prompt}
                              required
                              picked={flow[base.id as "B1" | "B2"]}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </Form.Item>
            ) : (
              <ChoiceField
                question={question}
                path={[question.id]}
                label={label}
                required={question.required}
                picked={values[question.id]}
              />
            )}
          </div>
        );
      })}

      <Space wrap>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          {submitted ? "Save changes" : "Submit"}
        </Button>
        {isEditing ? <Button onClick={() => setIsEditing(false)}>Cancel</Button> : null}
        {submitted ? null : <Text type="secondary">Your answers save as you go, so you can leave and come back.</Text>}
      </Space>
    </Form>
  );
}
